import React, { useRef, useCallback, useReducer } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { useTranslation } from '@/hooks/useTranslation';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import FloatingLabelTextarea from '@/components/Inputs/FloatingLabelTextarea';
import Turnstile from '@/components/Turnstile';
import SubmitButton from '@/components/Buttons/SubmitButton';
import { useToastStore } from '@/store';
import { feedbackApi } from '@/lib/api';
import SelectInput from '@/components/Inputs/SelectInput';
import StatusContainer from '@/components/StatusContainer';

// Create styles outside component
const styles = StyleSheet.create({
  formSection: {
    width: '100%',
    marginBottom: 20,
    gap: 16,
  },
  inputContainer: {
    gap: 16,
    width: '100%',
  },
});

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Platform check
const isWeb = Platform.OS === 'web';

// Form validation
const validateEmail = (email, t) => {
  if (!email.trim()) {
    return t('feedback.form.errors.emailRequired');
  }
  if (!EMAIL_REGEX.test(email.trim())) {
    return t('feedback.form.errors.invalidEmail');
  }
  return null;
};

const validateName = (name, t) => {
  if (!name.trim()) {
    return t('feedback.form.errors.nameRequired');
  }
  return null;
};

const validateMessage = (message, t) => {
  if (!message.trim()) {
    return t('feedback.form.errors.messageRequired');
  }
  if (message.trim().length < 10) {
    return t('feedback.form.errors.messageTooShort');
  }
  return null;
};

const validateReason = (reason, t) => {
  if (!reason || !reason.trim()) {
    return t('contact.selectReason');
  }
  return null;
};

// Form reducer
const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_NAME':
      return { ...state, name: action.payload, error: '' };
    case 'SET_EMAIL':
      return { ...state, email: action.payload, error: '' };
    case 'SET_REASON':
      return { ...state, reason: action.payload, error: '' };
    case 'SET_MESSAGE':
      return { ...state, message: action.payload, error: '' };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'RESET':
      return { ...initialFormState };
    default:
      return state;
  }
};

// Initial form state
const initialFormState = {
  name: '',
  email: '',
  reason: '',
  message: '',
  error: '',
  isLoading: false,
};

const ContactForm = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const turnstileRef = useRef(null);
  
  const [formState, dispatch] = useReducer(formReducer, initialFormState);

  const reasonOptions = [
    { label: t('contact.reasons.technicalIssues'), value: 'technical' },
    { label: t('contact.reasons.accountBilling'), value: 'billing' },
    { label: t('contact.reasons.enterprisePricing'), value: 'enterprise' },
    { label: t('contact.reasons.featureRequest'), value: 'feature' },
    { label: t('contact.reasons.bugReport'), value: 'bug' },
    { label: t('contact.reasons.generalFeedback'), value: 'feedback' },
    { label: t('contact.reasons.other'), value: 'other' },
  ];

  // Memoized input handlers
  const handleNameChange = useCallback((text) => {
    dispatch({ type: 'SET_NAME', payload: text });
  }, []);

  const handleEmailChange = useCallback((text) => {
    dispatch({ type: 'SET_EMAIL', payload: text });
  }, []);

  const handleReasonChange = useCallback((value) => {
    dispatch({ type: 'SET_REASON', payload: value });
  }, []);

  const handleMessageChange = useCallback((text) => {
    dispatch({ type: 'SET_MESSAGE', payload: text });
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const nameError = validateName(formState.name, t);
    if (nameError) {
      dispatch({ type: 'SET_ERROR', payload: nameError });
      return false;
    }

    const emailError = validateEmail(formState.email, t);
    if (emailError) {
      dispatch({ type: 'SET_ERROR', payload: emailError });
      return false;
    }

    const reasonError = validateReason(formState.reason, t);
    if (reasonError) {
      dispatch({ type: 'SET_ERROR', payload: reasonError });
      return false;
    }

    const messageError = validateMessage(formState.message, t);
    if (messageError) {
      dispatch({ type: 'SET_ERROR', payload: messageError });
      return false;
    }

    return true;
  }, [formState.name, formState.email, formState.reason, formState.message, t]);

  const handleSubmit = useCallback(async () => {
    try {
      if (!validateForm()) return;

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: '' });

      // Get turnstile token on submit
      let turnstileToken = null;
      if (isWeb) {
        try {
          turnstileToken = await turnstileRef.current?.execute();
        } catch (captchaError) {
          dispatch({ type: 'SET_ERROR', payload: t('feedback.form.errors.captchaRequired') });
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
      }

      // Submit using feedback API
      await feedbackApi.contactSubmit({
        name: formState.name.trim(),
        email: formState.email.trim(),
        reason: formState.reason,
        message: formState.message.trim(),
        ...(isWeb && { turnstileToken })
      });

      // Reset form on success
      dispatch({ type: 'RESET' });
      
      addToast(t('feedback.form.success'), 'success');
      onSuccess?.();
    } catch (error) {
      console.error('Error submitting contact form:', error);
      const errorMessage = error.message || t('feedback.form.error');
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      addToast(errorMessage, 'error');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [formState.name, formState.email, formState.reason, formState.message, t, addToast, onSuccess, validateForm]);

  return (
    <View style={styles.formSection}>
      <View style={styles.inputContainer}>
        <SelectInput
          label={t('contact.reasonLabel')}
          value={formState.reason}
          onValueChange={handleReasonChange}
          items={reasonOptions}
          isRequired
        />

        <FloatingLabelInput
          isFullWidth
          label={t('feedback.form.name.label')}
          value={formState.name}
          onChangeText={handleNameChange}
          returnKeyType="next"
          autoComplete="name"
          textContentType="name"
        />

        <FloatingLabelInput
          isFullWidth
          label={t('feedback.form.email.label')}
          value={formState.email}
          onChangeText={handleEmailChange}
          keyboardType="email-address"
          autoCapitalize="none"
          returnKeyType="next"
          autoComplete="email"
          textContentType="emailAddress"
        />

        <FloatingLabelTextarea
          label={t('feedback.form.message.label')}
          value={formState.message}
          onChangeText={handleMessageChange}
          height={150}
          multiline
          textAlignVertical="top"
        />

        {formState.error ? (
          <StatusContainer type="error" message={formState.error} />
        ) : null}

        {isWeb && <Turnstile ref={turnstileRef} />}

        <SubmitButton
          onPress={handleSubmit}
          isLoading={formState.isLoading}
          isDisabled={!formState.name.trim() || !formState.email.trim() || !formState.reason || !formState.message.trim()}
          title={t('feedback.form.submit')}
        />

      </View>
    </View>
  );
};

ContactForm.propTypes = {
  onSuccess: PropTypes.func,
};

export default React.memo(ContactForm);